import {Injectable} from '@angular/core';
import {Actions, Effect} from '@ngrx/effects';
import {Observable} from 'rxjs/Observable';
import {Action, Store} from '@ngrx/store';
import * as user from '../actions/user';
import * as auth from '../../core/actions/authentication';
import * as routerActions from '../actions/router';
import { of } from 'rxjs/observable/of';
import {UserService} from '../services/user';
import {User} from '../models/user';
import { UserAgreementService } from 'app/modules/core/services/user-agreement';
import { ErrorModel } from '../models/error-model';

@Injectable()
export class UserEffects {
  @Effect()
  loadUserPage$: Observable<Action> = this.actions$
    .ofType(user.LOAD_USER_LIST)
    .map((action: user.LoadUserListAction) => action.payload)
    .switchMap(pm => {

      const nextLookup$ = this.actions$.ofType(user.LOAD_USER_LIST).skip(1);
      const errorMessage = 'Error loading user list.';
      return this.userService.getUsers(pm)
        .takeUntil(nextLookup$)
        .map(u => new user.LoadUserListCompleteAction(u))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  loadRoles$: Observable<Action> = this.actions$
    .ofType(user.LOAD_USER_ROLES)
    .switchMap(() => {
      const nextLookup$ = this.actions$.ofType(user.LOAD_USER_ROLES).skip(1);
      const errorMessage = 'Error loading user roles.';
      return this.userService.getRoles()
        .takeUntil(nextLookup$)
        .map(r => new user.LoadUserRolesCompleteAction(r))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  loadUserDetails$: Observable<Action> = this.actions$
    .ofType(user.LOAD_USER_DETAILS)
    .map((action: user.LoadUserDetailsAction) => action.payload)
    .switchMap(un => {
      const nextLookup$ = this.actions$.ofType(user.LOAD_USER_DETAILS).skip(1);
      const errorMessage = 'Error loading user details.';
      if (!un) {
        return Observable.of(new user.LoadUserDetailsCompleteAction(new User(null, null, null, null, null, true)));
      } else {
        return this.userService.loadUser(un)
          .takeUntil(nextLookup$)
          .map(u => new user.LoadUserDetailsCompleteAction(u))
          .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
      }
    });

  @Effect()
  saveUserDetails$: Observable<Action> = this.actions$
    .ofType(user.SAVE_USER_DETAILS)
    .map((action: user.SaveUserDetailsAction) => action.payload)
    .switchMap(d => {

      const nextLookup$ = this.actions$.ofType(user.SAVE_USER_DETAILS).skip(1);
        return this.userService.saveUser(d)
          .takeUntil(nextLookup$)
          .map(m => {
            if (!m) {
              return new user.SaveUserDetailsCompleteAction(m);
            } else {
              return new auth.ErrorAction(new ErrorModel({}, m));
            }
          })
          .catch(e => of(new auth.ErrorAction(new ErrorModel(e, 'Error Creating User.'))));
    });

  @Effect()
  saveUserDetailsComplete$: Observable<Action> = this.actions$
    .ofType(user.SAVE_USER_DETAILS_COMPLETE)
    .map((action: user.SaveUserDetailsCompleteAction) => action.payload)
    .switchMap(m => of(new routerActions.Go({path: ['admin', 'users']})));

  @Effect()
  checkUsername$: Observable<Action> = this.actions$
    .ofType(user.CHECK_USERNAME_EXISTS)
    .map((action: user.CheckUsernameExistsAction) => action.payload)
    .switchMap(un => {
      const nextLookup$ = this.actions$.ofType(user.CHECK_USERNAME_EXISTS).skip(1);
      const errorMessage = 'Error checking username.';
      return this.userService.checkIfUsernameExists(un)
        .takeUntil(nextLookup$)
        .map(u => new user.CheckUsernameExistsCompleteAction(u))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  checkEmail$: Observable<Action> = this.actions$
    .ofType(user.CHECK_EMAIL_EXISTS)
    .map((action: user.CheckEmailExistsAction) => action.payload)
    .switchMap(un => {
      const nextLookup$ = this.actions$.ofType(user.CHECK_EMAIL_EXISTS).skip(1);
      const errorMessage = 'Error checking email.';
      return this.userService.checkIfEmailExists(un)
        .takeUntil(nextLookup$)
        .map(u => new user.CheckEmailExistsCompleteAction(u))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  toggleEnabled$: Observable<Action> = this.actions$
    .ofType(user.TOGGLE_ENABLED)
    .map((action: user.ToggleEnabledAction) => action.payload)
    .switchMap(un => {
      const nextLookup$ = this.actions$.ofType(user.TOGGLE_ENABLED).skip(1);
      const errorMessage = 'Error changing enabled status of user.';
      return this.userService.toggleEnabled(un.toString())
        .takeUntil(nextLookup$)
        .map(u => new user.ToggleEnabledCompleteAction(un))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

  @Effect()
  acceptAgreement$: Observable<Action> = this.actions$
    .ofType(user.ACCEPT_USER_AGREEMENT)
    .switchMap(() => {
      const nextLookup$ = this.actions$.ofType(user.ACCEPT_USER_AGREEMENT).skip(1);
      const errorMessage = 'Error accepting user agreement.';
      return this.userAgreementService.accept()
        .takeUntil(nextLookup$)
        .map(u => new user.AcceptUserAgreementCompleteAction(u))
        .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
    });

    @Effect()
  acceptAgreementComplete$: Observable<Action> = this.actions$
    .ofType(user.ACCEPT_USER_AGREEMENT_COMPLETE)
    .map((a: user.AcceptUserAgreementCompleteAction) => a.payload)
    .map(a => {
        if (a) {
          return new routerActions.Go({path: ['/']});
        } else {
          return new auth.ErrorAction(new ErrorModel({}, 'Error accepting agreement'));
        }
    });

    @Effect()
    registerMfa$: Observable<Action> = this.actions$
        .ofType(user.REGISTER_MFA)
        .map((action: user.RegisterMfaAction) => action.payload)
        .switchMap(usr => {
            const nextLookup$ = this.actions$.ofType(user.REGISTER_MFA).skip(1);
            const errorMessage = 'Error registering MFA for User.';
            return this.userService.registerMfa(usr)
                .takeUntil(nextLookup$)
                .map(u => new user.RegisterMfaCompleteAction(u))
                .catch(e => of(new auth.ErrorAction(new ErrorModel(e, errorMessage))));
        });



    @Effect()
    registerMfaCompleted$: Observable<Action> = this.actions$
        .ofType(user.REGISTER_MFA_COMPLETE)
        .map((a: user.RegisterMfaCompleteAction) => a.payload)
        .map(a => {
            if (a) {
                alert('MFA Token Sent to User');
                return new routerActions.Go({path: ['/core/admin/users']});
            } else {
                alert('Error creating MFA token');
                return new auth.ErrorAction(new ErrorModel({}, 'Error accepting agreement'));
            }
        });

  constructor(private actions$: Actions,
              private userService: UserService,
              private userAgreementService: UserAgreementService) {}

}



// WEBPACK FOOTER //
// C:/Users/jstan/Projects/usda-document-client/src/app/modules/core/effects/user.ts